import type { FeatureFlags } from '@atlaskit/editor-common/types';
import type { UserPreferencesProvider } from '@atlaskit/editor-common/user-preferences';
import type { EditorProps } from '@atlaskit/editor-core';
import type { TrackChangesPluginOptions } from '@atlaskit/editor-plugin-track-changes';
import { accessibilityUtilsPlugin } from '@atlaskit/editor-plugin-accessibility-utils';
import { contentInsertionPlugin } from '@atlaskit/editor-plugin-content-insertion';
import { batchAttributeUpdatesPlugin } from '@atlaskit/editor-plugin-batch-attribute-updates';
import { borderPlugin } from '@atlaskit/editor-plugin-border';
import { featureFlagsPlugin } from '@atlaskit/editor-plugin-feature-flags';
import { cardPlugin } from '@atlaskit/editor-plugin-card';
import { codeBidiWarningPlugin } from '@atlaskit/editor-plugin-code-bidi-warning';
import { customAutoformatPlugin } from '@atlaskit/editor-plugin-custom-autoformat';
import { dataConsumerPlugin } from '@atlaskit/editor-plugin-data-consumer';
import { datePlugin } from '@atlaskit/editor-plugin-date';
import { emojiPlugin } from '@atlaskit/editor-plugin-emoji';
import { expandPlugin } from '@atlaskit/editor-plugin-expand';
import { scrollIntoViewPlugin } from '@atlaskit/editor-plugin-scroll-into-view';
import { gridPlugin } from '@atlaskit/editor-plugin-grid';
import { helpDialogPlugin } from '@atlaskit/editor-plugin-help-dialog';
import { insertBlockPlugin } from '@atlaskit/editor-plugin-insert-block';
import { listPlugin } from '@atlaskit/editor-plugin-list';
import { mediaPlugin } from '@atlaskit/editor-plugin-media';
import { mediaInsertPlugin } from '@atlaskit/editor-plugin-media-insert';
import { mentionsPlugin } from '@atlaskit/editor-plugin-mentions';
import { panelPlugin } from '@atlaskit/editor-plugin-panel';
import { pasteOptionsToolbarPlugin } from '@atlaskit/editor-plugin-paste-options-toolbar';
import { rulePlugin } from '@atlaskit/editor-plugin-rule';
import { statusPlugin } from '@atlaskit/editor-plugin-status';
import { tablePlugin } from '@atlaskit/editor-plugin-table';
import { pastePlugin } from '@atlaskit/editor-plugin-paste';
import { betterTypeHistoryPlugin } from '@atlaskit/editor-plugin-better-type-history';
import { clipboardPlugin } from '@atlaskit/editor-plugin-clipboard';
import { textColorPlugin } from '@atlaskit/editor-plugin-text-color';
import { toolbarListsIndentationPlugin } from '@atlaskit/editor-plugin-toolbar-lists-indentation';
import { hyperlinkPlugin } from '@atlaskit/editor-plugin-hyperlink';
import { breakoutPlugin } from '@atlaskit/editor-plugin-breakout';
import { alignmentPlugin } from '@atlaskit/editor-plugin-alignment';
import { guidelinePlugin } from '@atlaskit/editor-plugin-guideline';
import { layoutPlugin } from '@atlaskit/editor-plugin-layout';
import { extensionPlugin } from '@atlaskit/editor-plugin-extension';
import { tasksAndDecisionsPlugin } from '@atlaskit/editor-plugin-tasks-and-decisions';
import { findReplacePlugin } from '@atlaskit/editor-plugin-find-replace';
import { analyticsPlugin } from '@atlaskit/editor-plugin-analytics';
import { focusPlugin } from '@atlaskit/editor-plugin-focus';
import { fragmentPlugin } from '@atlaskit/editor-plugin-fragment';
import { EditorPresetBuilder } from '@atlaskit/editor-common/preset';
import { compositionPlugin } from '@atlaskit/editor-plugin-composition';
import { contextIdentifierPlugin } from '@atlaskit/editor-plugin-context-identifier';
import type { ScrollGutterPluginOptions } from '@atlaskit/editor-plugin-base';
import { basePlugin } from '@atlaskit/editor-plugin-base';
import { decorationsPlugin } from '@atlaskit/editor-plugin-decorations';
import { typeAheadPlugin } from '@atlaskit/editor-plugin-type-ahead';
import { captionPlugin } from '@atlaskit/editor-plugin-caption';
import { indentationPlugin } from '@atlaskit/editor-plugin-indentation';
import { localIdPlugin } from '@atlaskit/editor-plugin-local-id';
import { clearMarksOnEmptyDocPlugin } from '@atlaskit/editor-plugin-clear-marks-on-empty-doc';
import { selectionToolbarPlugin } from '@atlaskit/editor-plugin-selection-toolbar';
import { textFormattingPlugin } from '@atlaskit/editor-plugin-text-formatting';
import { quickInsertPlugin } from '@atlaskit/editor-plugin-quick-insert';
import { placeholderPlugin } from '@atlaskit/editor-plugin-placeholder';
import { copyButtonPlugin } from '@atlaskit/editor-plugin-copy-button';
import { unsupportedContentPlugin } from '@atlaskit/editor-plugin-unsupported-content';
import { editorDisabledPlugin } from '@atlaskit/editor-plugin-editor-disabled';
import { floatingToolbarPlugin } from '@atlaskit/editor-plugin-floating-toolbar';
import { toolbarPlugin } from '@atlaskit/editor-plugin-toolbar';
import { selectionPlugin } from '@atlaskit/editor-plugin-selection';
import { codeBlockPlugin } from '@atlaskit/editor-plugin-code-block';
import { codeBlockAdvancedPlugin } from '@atlaskit/editor-plugin-code-block-advanced';
import { primaryToolbarPlugin } from '@atlaskit/editor-plugin-primary-toolbar';
import { blockTypePlugin } from '@atlaskit/editor-plugin-block-type';
import { widthPlugin } from '@atlaskit/editor-plugin-width';
import { ufoPlugin } from '@atlaskit/editor-plugin-ufo';
import { contextPanelPlugin } from '@atlaskit/editor-plugin-context-panel';
import { placeholderTextPlugin } from '@atlaskit/editor-plugin-placeholder-text';
import { highlightPlugin } from '@atlaskit/editor-plugin-highlight';
import { blockControlsPlugin } from '@atlaskit/editor-plugin-block-controls';
import { userPreferencesPlugin } from '@atlaskit/editor-plugin-user-preferences';
import { undoRedoPlugin } from '@atlaskit/editor-plugin-undo-redo';
import { historyPlugin } from '@atlaskit/editor-plugin-history';
import { showDiffPlugin } from '@atlaskit/editor-plugin-show-diff';
import { trackChangesPlugin } from '@atlaskit/editor-plugin-track-changes';

import { fg } from '@confluence/feature-gating';

import { createFeatureFlagsFromProps } from '../utils/createFeatureFlagsFromProps.ts';

export type CreateSnippetEditorPresetOptions = {
	appearance: EditorProps['appearance'];
	media: EditorProps['media'];
	tableOptions: EditorProps['allowTables'];
	expandOptions: EditorProps['allowExpand'];
	layoutOptions: EditorProps['allowLayouts'];
	placeholder: EditorProps['placeholder'];
	emojiProvider: EditorProps['emojiProvider'];
	mentionProvider: EditorProps['mentionProvider'];
	featureFlags: EditorProps['featureFlags'];
	linking: EditorProps['linking'];
	autoformattingProvider: EditorProps['autoformattingProvider'];
	taskDecisionProvider: EditorProps['taskDecisionProvider'];
	contextIdentifierProvider: EditorProps['contextIdentifierProvider'];
	trackChangesPluginOptions?: TrackChangesPluginOptions;
	editorPreferencesProvider?: UserPreferencesProvider;
};

/**
 * Creates a preset for the snippet editor (e.g. Create Snippet modal).
 * Mirrors the smart-create preset but omits the referentiality plugin so this file can be modified independently.
 */
export function createSnippetEditorPreset(options: CreateSnippetEditorPresetOptions): any {
	const appearance = options.appearance;
	const featureFlags: FeatureFlags = createFeatureFlagsFromProps(options.featureFlags);
	const getEditorFeatureFlags = () => featureFlags;

	const cardOptions = options.linking?.smartLinks;

	return new EditorPresetBuilder()
		.add([featureFlagsPlugin, featureFlags || {}])
		.add([
			toolbarPlugin,
			{
				contextualFormattingEnabled: 'always-pinned',
			},
		])
		.add([analyticsPlugin, {}])
		.add(betterTypeHistoryPlugin)
		.add([pastePlugin, { cardOptions, sanitizePrivateContent: true, isFullPage: true }])
		.add(clipboardPlugin)
		.add(focusPlugin)
		.add(compositionPlugin)
		.add([
			contextIdentifierPlugin,
			{ contextIdentifierProvider: options.contextIdentifierProvider },
		])
		.add([
			basePlugin,
			{
				allowInlineCursorTarget: true,
				allowScrollGutter: {
					getScrollElement: () => document.querySelector('.fabric-editor-popup-scroll-parent'),
				} as ScrollGutterPluginOptions,
			},
		])
		.maybeAdd(localIdPlugin, fg('platform_editor_adf_with_localid'))
		.maybeAdd(
			[
				userPreferencesPlugin,
				{
					initialUserPreferences: { toolbarDockingPosition: 'top' },
					userPreferencesProvider: undefined,
				},
			],
			fg('platform_editor_use_preferences_plugin'),
		)
		.add(decorationsPlugin)
		.add([
			typeAheadPlugin,
			{
				isMobile: false,
			},
		])
		.add([primaryToolbarPlugin, { contextualFormattingEnabled: true }])
		.add([
			blockTypePlugin,
			{
				includeBlockQuoteAsTextstyleOption: true,
				lastNodeMustBeParagraph: false,
				isUndoRedoButtonsEnabled: undefined,
				allowBlockType: undefined,
			},
		])
		.add(clearMarksOnEmptyDocPlugin)
		.add([
			selectionToolbarPlugin,
			{
				preferenceToolbarAboveSelection: true,
				contextualFormattingEnabled: true,
			},
		])
		.add([
			hyperlinkPlugin,
			{
				editorAppearance: appearance,
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				onClickCallback: cardOptions?.onClickCallback,
				platform: 'web',
			},
		])
		.add([
			textFormattingPlugin,
			{
				responsiveToolbarMenu: undefined,
			},
		])
		.add(widthPlugin)
		.add([
			quickInsertPlugin,
			{
				enableElementBrowser: true,
				disableDefaultItems: false,
				headless: false,
			},
		])
		.add([placeholderPlugin, { placeholder: options.placeholder }])
		.add(unsupportedContentPlugin)
		.add(editorDisabledPlugin)
		.add(copyButtonPlugin)
		.add(floatingToolbarPlugin)
		.add([selectionPlugin, { useLongPressSelection: false }])
		.add([
			codeBlockPlugin,
			{
				allowCopyToClipboard: true,
				useLongPressSelection: false,
				allowCompositionInputOverride: false,
			},
		])
		.add(ufoPlugin)
		.add(dataConsumerPlugin)
		.add(accessibilityUtilsPlugin)
		.add(contentInsertionPlugin)
		.add(batchAttributeUpdatesPlugin)
		.add([breakoutPlugin, { allowBreakoutButton: appearance === 'full-page' }])
		.add(alignmentPlugin)
		.add([textColorPlugin, true])
		.add(listPlugin)
		.add(rulePlugin)
		.add([
			expandPlugin,
			{
				...(typeof options.expandOptions === 'object' ? options.expandOptions : {}),
				useLongPressSelection: false,
				appearance,
				allowInteractiveExpand: true,
			},
		])
		.add(guidelinePlugin)
		.add([gridPlugin, { shouldCalcBreakoutGridLines: true }])
		.add([
			mediaPlugin,
			{
				...options.media,
				allowLazyLoading: true,
				allowBreakoutSnapPoints: true,
				allowAdvancedToolBarOptions:
					typeof options.media?.allowAdvancedToolBarOptions !== 'undefined'
						? options.media?.allowAdvancedToolBarOptions
						: true,
				allowCommentsOnMedia: false,
				allowDropzoneDropLine: true,
				allowMediaSingleEditable: true,
				allowRemoteDimensionsFetch: true,
				allowMarkingUploadsAsIncomplete: false,
				allowImagePreview: true,
				fullWidthEnabled: appearance === 'full-width',
				editorAppearance: appearance,
				uploadErrorHandler: undefined,
				waitForMediaUpload: true,
				isCopyPasteEnabled: true,
				alignLeftOnInsert:
					typeof options.media?.alignLeftOnInsert !== 'undefined'
						? options.media?.alignLeftOnInsert
						: false,
				getEditorFeatureFlags,
			},
		])
		.add(mediaInsertPlugin)
		.add(captionPlugin)
		.add([
			mentionsPlugin,
			{
				mentionProvider: options.mentionProvider,
				sanitizePrivateContent: true,
				allowZeroWidthSpaceAfter: true,
				profilecardProvider: undefined,
			},
		])
		.add([
			emojiPlugin,
			{
				emojiProvider: options.emojiProvider,
			},
		])
		.add([
			tablePlugin,
			{
				tableOptions:
					!options.tableOptions || typeof options.tableOptions === 'boolean'
						? {}
						: options.tableOptions,
				dragAndDropEnabled: featureFlags?.tableDragAndDrop,
				isTableScalingEnabled: true,
				allowContextualMenu: true,
				fullWidthEnabled: appearance === 'full-width',
				wasFullWidthEnabled: undefined,
				getEditorFeatureFlags,
				isCommentEditor: false,
				isChromelessEditor: false,
			},
		])
		.add(fragmentPlugin)
		.add([
			tasksAndDecisionsPlugin,
			{
				allowNestedTasks: true,
				consumeTabs: true,
				useLongPressSelection: false,
				taskDecisionProvider: options.taskDecisionProvider,
			},
		])
		.add([
			panelPlugin,
			{
				useLongPressSelection: false,
				allowCustomPanel: true,
				allowCustomPanelEdit: true,
			},
		])
		.add([helpDialogPlugin, false])
		.add(contextPanelPlugin)
		.add([
			extensionPlugin,
			{
				breakoutEnabled: appearance === 'full-page',
				useLongPressSelection: false,
				appearance,
			},
		])
		.add([
			datePlugin,
			{
				weekStartDay: undefined,
			},
		])
		.add([placeholderTextPlugin, {}])
		.add([
			layoutPlugin,
			{
				...(typeof options.layoutOptions === 'object' ? options.layoutOptions : {}),
				useLongPressSelection: false,
				UNSAFE_allowSingleColumnLayout: undefined,
				editorAppearance: appearance,
			},
		])
		.add([
			cardPlugin,
			{
				...options.linking?.smartLinks,
				fullWidthMode: appearance === 'full-width',
				linkPicker: options.linking?.linkPicker,
				lpLinkPicker: featureFlags.lpLinkPicker ?? false,
				editorAppearance: appearance,
			},
		])
		.add([
			customAutoformatPlugin,
			{
				autoformattingProvider: options.autoformattingProvider,
			},
		])
		.add([
			statusPlugin,
			{
				menuDisabled: false,
				allowZeroWidthSpaceAfter: true,
			},
		])
		.add(indentationPlugin)
		.add(scrollIntoViewPlugin)
		.add([
			toolbarListsIndentationPlugin,
			{
				showIndentationButtons: true,
				allowHeadingAndParagraphIndentation: true,
			},
		])
		.add([
			insertBlockPlugin,
			{
				allowTables: true,
				allowExpand: true,
				horizontalRuleEnabled: true,
				tableSelectorSupported: featureFlags?.tableSelector,
				nativeStatusSupported: true,
				showElementBrowserLink: true,
				appearance,
			},
		])
		.add([
			findReplacePlugin,
			{
				takeFullWidth: false,
				twoLineEditorToolbar: !!featureFlags.twoLineEditorToolbar,
			},
		])
		.add(borderPlugin)
		.add(pasteOptionsToolbarPlugin)
		.add([
			codeBidiWarningPlugin,
			{
				appearance,
			},
		])
		.add(highlightPlugin)
		.add(codeBlockAdvancedPlugin)
		.add(blockControlsPlugin)
		.add(historyPlugin)
		.add(undoRedoPlugin)
		.add(showDiffPlugin)
		.add([
			trackChangesPlugin,
			{
				showOnToolbar: fg('platform_editor_toolbar_aifc_undo_redo_confluence'),
				ButtonWrapper: options.trackChangesPluginOptions?.ButtonWrapper,
			},
		]);
}
